import { ModuleCard, ModuleStatus } from "@/components/academician/module-card"
import { Progress } from "@/components/ui/progress"

export interface AssetCounts {
  Video: number
  Quiz: number
  BrainstormingSession: number
  MeetupSession: number
  Activity: number
  Document: number
}

export interface Module {
  id: string
  number: number
  title: string
  description: string
  chapterCount: number
  gatedChapterCount?: number
  nonGatedChapterCount?: number
  assetCounts?: AssetCounts
  duration: number
  progress: number
  status: ModuleStatus
  href?: string
}

export interface ModulesPageProps {
  /**
   * Page title
   */
  title?: string

  /**
   * Cohort name
   */
  cohortName?: string

  /**
   * List of modules
   */
  modules?: Module[]

  /**
   * Number of completed modules
   */
  completedModules?: number

  /**
   * Total number of modules
   */
  totalModules?: number

  /**
   * Overall progress percentage
   */
  overallProgress?: number

  /**
   * Module click handler
   */
  onModuleClick?: (moduleId: string) => void

  /**
   * Additional CSS classes
   */
  className?: string
}

// Default modules data
const defaultModules: Module[] = [
  {
    id: "1",
    number: 1,
    title: "Customer Discovery Fundamentals",
    description: "Learn the basics of customer discovery, problem validation, and how to conduct effective customer interviews to validate your startup idea.",
    chapterCount: 5,
    duration: 45,
    progress: 100,
    status: "completed",
    href: "/founder/modules"
  },
  {
    id: "2",
    number: 2,
    title: "Problem Validation Techniques",
    description: "Master advanced techniques for validating customer problems, identifying pain points, and ensuring you're solving a real problem worth solving.",
    chapterCount: 6,
    duration: 52,
    progress: 67,
    status: "in-progress",
    href: "/founder/modules"
  },
  {
    id: "3",
    number: 3,
    title: "Solution Validation & MVP",
    description: "Learn how to validate your solution, build a minimum viable product, and test your assumptions with real customers before investing heavily.",
    chapterCount: 7,
    duration: 58,
    progress: 0,
    status: "locked"
  },
  {
    id: "4",
    number: 4,
    title: "Market Sizing & Opportunity",
    description: "Understand how to size your market, identify your target segments, and evaluate the business opportunity for your startup idea.",
    chapterCount: 5,
    duration: 48,
    progress: 0,
    status: "locked"
  },
  {
    id: "5",
    number: 5,
    title: "Business Model Design",
    description: "Design a sustainable business model, understand revenue streams, and create a path to profitability for your startup.",
    chapterCount: 6,
    duration: 55,
    progress: 0,
    status: "locked"
  },
  {
    id: "6",
    number: 6,
    title: "Go-to-Market Strategy",
    description: "Develop a comprehensive go-to-market strategy, identify your channels, and plan your customer acquisition approach.",
    chapterCount: 7,
    duration: 62,
    progress: 0,
    status: "locked"
  },
  {
    id: "7",
    number: 7,
    title: "Fundraising Fundamentals",
    description: "Learn the basics of fundraising, pitch deck creation, investor relations, and how to prepare for your first funding round.",
    chapterCount: 8,
    duration: 70,
    progress: 0,
    status: "locked"
  },
  {
    id: "8",
    number: 8,
    title: "Scaling Your Startup",
    description: "Understand how to scale your operations, build your team, and grow your startup from early traction to sustainable growth.",
    chapterCount: 9,
    duration: 75,
    progress: 0,
    status: "locked"
  }
]

export function ModulesPage({
  title = "Learning Modules",
  cohortName = "Startup Validation Cohort - Spring 2024",
  modules = defaultModules,
  completedModules,
  totalModules,
  overallProgress,
  onModuleClick,
  className
}: ModulesPageProps) {
  // Calculate completed modules if not provided
  const completed = completedModules ?? modules.filter(m => m.status === "completed").length
  const total = totalModules ?? modules.length
  const progress = overallProgress ?? Math.round((completed / total) * 100)

  return (
    <div className={className}>
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Page Title */}
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {title}
          </h1>

          {/* Cohort Name */}
          <p className="text-sm text-muted-foreground mb-6">
            {cohortName}
          </p>

          {/* Overall Progress */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">
                Overall Progress
              </span>
              <span className="text-sm font-semibold text-foreground">
                {completed} of {total} modules complete
              </span>
            </div>
            <Progress value={progress} className="h-3" />
            <p className="text-xs text-muted-foreground">
              {progress}% complete
            </p>
          </div>
        </div>
      </div>

      {/* Module List */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-4">
          {modules.map((module) => (
            <ModuleCard
              key={module.id}
              number={module.number}
              title={module.title}
              description={module.description}
              chapterCount={module.chapterCount}
              gatedChapterCount={module.gatedChapterCount}
              nonGatedChapterCount={module.nonGatedChapterCount}
              assetCounts={module.assetCounts}
              duration={module.duration}
              progress={module.progress}
              status={module.status}
              href={module.href}
              onClick={onModuleClick ? () => onModuleClick(module.id) : undefined}
            />
          ))}
        </div>

        {/* Empty State */}
        {modules.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              No modules available yet. Check back soon!
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
